/**
 * Visual screens editor for a WhatsApp Flow — no JSON. Add screens, add text or
 * questions from a friendly menu (each with a plain-language hint), reorder by drag,
 * and set the footer button + routing. Selecting a screen drives the live preview.
 */

import { IconGripVertical, IconTrash, IconPlus, IconArrowDown } from '@tabler/icons-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Stack, Group, Text, Button, TextInput, Select, ActionIcon, Badge, Switch, Divider,
} from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import {
  type FlowScreen, type FlowField, type FieldType,
  FIELD_CATALOG, DISPLAY_TYPES, INPUT_TYPES, isDisplay, fieldLabel, newScreen, newField,
} from '../../_shared/flowDefinition';

const addMenuData = [
  { group: 'Text', items: DISPLAY_TYPES.map((t) => ({ value: t, label: FIELD_CATALOG.find((c) => c.value === t)!.label })) },
  { group: 'Questions', items: INPUT_TYPES.map((t) => ({ value: t, label: FIELD_CATALOG.find((c) => c.value === t)!.label })) },
];

function FieldEditor({ field, onChange, onRemove }: { field: FlowField; onChange: (patch: Partial<FlowField>) => void; onRemove: () => void }) {
  const spec = FIELD_CATALOG.find((c) => c.value === field.type);
  return (
    <Card withBorder padding="xs" radius="sm" bg="var(--mantine-color-default-hover)">
      <Group justify="space-between" mb={4} wrap="nowrap">
        <Badge size="xs" variant="light">{fieldLabel(field.type)}</Badge>
        <ActionIcon size="sm" color="red" variant="subtle" onClick={onRemove} title="Remove"><IconTrash size={14} /></ActionIcon>
      </Group>
      {spec?.hint && <Text size="9px" c="dimmed" mb={6}>{spec.hint}</Text>}
      {isDisplay(field.type) ? (
        <TextInput size="xs" placeholder={spec?.example || 'Text'} value={field.text} onChange={(e) => onChange({ text: e.currentTarget.value })} />
      ) : (
        <Stack gap={4}>
          <Group grow>
            <TextInput size="xs" label="Question / label" placeholder={spec?.example || 'Label'} value={field.label} onChange={(e) => onChange({ label: e.currentTarget.value })} />
            <TextInput size="xs" label="Saved as" placeholder="field_name" value={field.name} onChange={(e) => onChange({ name: e.currentTarget.value })} />
          </Group>
          {(field.type === 'Dropdown' || field.type === 'RadioButtonsGroup') && (
            <TextInput size="xs" label="Options (comma-separated)" placeholder="Option 1, Option 2, Option 3" value={field.options} onChange={(e) => onChange({ options: e.currentTarget.value })} />
          )}
          <Switch size="xs" label="Required" checked={field.required} onChange={(e) => onChange({ required: e.currentTarget.checked })} />
        </Stack>
      )}
    </Card>
  );
}

function SortableScreen({ screen, index, total, screens, active, onSelect, onChange, onRemove }: {
  screen: FlowScreen; index: number; total: number; screens: FlowScreen[]; active: boolean;
  onSelect: () => void; onChange: (s: FlowScreen) => void; onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: screen.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  const addField = (type: FieldType) => onChange({ ...screen, fields: [...screen.fields, newField(type, screen.fields.length)] });
  const updateField = (fid: string, patch: Partial<FlowField>) => onChange({ ...screen, fields: screen.fields.map((f) => (f.id === fid ? { ...f, ...patch } : f)) });
  const removeField = (fid: string) => onChange({ ...screen, fields: screen.fields.filter((f) => f.id !== fid) });

  return (
    <div ref={setNodeRef} style={style}>
      <Card withBorder padding="md" radius="md" onClick={onSelect} style={{ cursor: 'pointer', borderColor: active ? 'var(--mantine-color-teal-5)' : undefined }}>
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" style={{ flex: 1 }}>
              <ActionIcon variant="subtle" color="gray" {...attributes} {...listeners} style={{ cursor: 'grab' }} title="Drag to reorder" onClick={(e) => e.stopPropagation()}>
                <IconGripVertical size={16} />
              </ActionIcon>
              <Badge variant="light" color="teal">Screen {index + 1}</Badge>
              <TextInput placeholder="Screen title" value={screen.title} onChange={(e) => onChange({ ...screen, title: e.currentTarget.value })} style={{ flex: 1 }} onClick={(e) => e.stopPropagation()} />
            </Group>
            <ActionIcon color="red" variant="subtle" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Delete screen" disabled={total <= 1}><IconTrash size={16} /></ActionIcon>
          </Group>

          {screen.fields.map((f) => (
            <div key={f.id} onClick={(e) => e.stopPropagation()}>
              <FieldEditor field={f} onChange={(patch) => updateField(f.id, patch)} onRemove={() => removeField(f.id)} />
            </div>
          ))}

          <Select
            size="xs"
            placeholder="+ Add text or a question"
            data={addMenuData}
            value={null}
            onChange={(v) => v && addField(v as FieldType)}
            searchable
            onClick={(e) => e.stopPropagation()}
          />

          <Divider label="When the button is tapped" labelPosition="left" />
          <Group grow>
            <TextInput size="xs" label="Button text" value={screen.footerLabel} onChange={(e) => onChange({ ...screen, footerLabel: e.currentTarget.value })} onClick={(e) => e.stopPropagation()} />
            <Select size="xs" label="Action" data={[{ value: 'navigate', label: 'Go to next screen' }, { value: 'complete', label: 'Submit & finish' }]} value={screen.action} onChange={(v) => onChange({ ...screen, action: (v as 'navigate' | 'complete') || 'complete' })} onClick={(e) => e.stopPropagation()} />
            {screen.action === 'navigate' && (
              <Select size="xs" label="Next screen" placeholder="Select" data={screens.filter((s) => s.id !== screen.id).map((s) => ({ value: s.id, label: s.title || `Screen ${screens.indexOf(s) + 1}` }))} value={screen.nextId} onChange={(v) => onChange({ ...screen, nextId: v })} onClick={(e) => e.stopPropagation()} />
            )}
          </Group>
        </Stack>
      </Card>
      {index < total - 1 && <Group justify="center" my={4}><IconArrowDown size={18} color="var(--mantine-color-dimmed)" /></Group>}
    </div>
  );
}

export function FlowScreensEditor({ screens, onChange, activeId, onSelect }: {
  screens: FlowScreen[];
  onChange: (screens: FlowScreen[]) => void;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldIndex = screens.findIndex((s) => s.id === active.id);
      const newIndex = screens.findIndex((s) => s.id === over.id);
      onChange(arrayMove(screens, oldIndex, newIndex));
    }
  };

  const updateScreen = (s: FlowScreen) => onChange(screens.map((x) => (x.id === s.id ? s : x)));
  const removeScreen = (id: string) => onChange(screens.filter((x) => x.id !== id));
  const addScreen = () => onChange([...screens, newScreen(screens.length)]);

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" fw={500}>Screens</Text>
        <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={addScreen}>Add screen</Button>
      </Group>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={screens.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <Stack gap={0}>
            {screens.map((s, i) => (
              <SortableScreen
                key={s.id}
                screen={s}
                index={i}
                total={screens.length}
                screens={screens}
                active={s.id === activeId}
                onSelect={() => onSelect(s.id)}
                onChange={updateScreen}
                onRemove={() => removeScreen(s.id)}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Stack>
  );
}
